import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import withErrorHandler from '../../../hocs/withErrorHandler';
import axios from '../../../httpClients/axios-datamerch';
import * as actions from '../../../store/actions';

import { BrokersTable } from '../../../components';
import {
  CircularProgress,
  Backdrop,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Button
} from '@mui/material';
import useClasses from '../../../helpers/useClasses';

const styles = theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  },
  backdrop: {
    zIndex: '100'
  },
  upgrade: {
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  upgradeButton: {
    marginTop: theme.spacing(2)
  }
});

const BrokerList = props => {
  const classes = useClasses(styles);

  const {
    onFetchBrokers,
    onFetchBrokerNotes,
    brokers,
    loading,
    pagination,
    broker_notes,
    notes_loading,
    plan_level,
    is_admin
  } = props;

  const isPro = plan_level === 'PRO';

  const [page, setPage] = useState(0);
  const [per_page, setPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);
  const [orderBy, setOrderBy] = useState('note_count');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    if (isPro) {
      onFetchBrokers(page, per_page, search, `${orderBy} ${order.toUpperCase()}`);
    }
  }, [onFetchBrokers, isPro, page, per_page, search, orderBy, order]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePerPageChange = (event) => {
    setPerPage(event.target.value);
    setPage(0);
  };

  const handleSearchClick = (event, newSearch) => {
    setIsFiltered(true);
    setPage(0);
    setSearch(newSearch);
  };

  const handleSearchCancelClick = (event) => {
    setIsFiltered(false);
    setPage(0);
    setSearch('');
  };

  const handleRequestSort = (field) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      // Sensible default direction per field: A–Z for the name, largest/newest first otherwise.
      setOrder(field === 'broker_name' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const handleExpandBroker = (broker_name) => {
    onFetchBrokerNotes(broker_name);
  };

  let content = (
    <Backdrop className={classes.backdrop} open={true}>
      <CircularProgress color="inherit" />
    </Backdrop>
  );

  if (!isPro) {
    content = (
      <Card>
        <CardHeader title="Brokers" />
        <Divider />
        <CardContent className={classes.upgrade}>
          <Typography variant="h5" gutterBottom>The brokers list is a PRO feature</Typography>
          {is_admin ? (
            <React.Fragment>
              <Typography variant="body1" color="textSecondary">
                This feature is only available to PRO members. Upgrade your account to PRO to
                see the brokers referenced on merchant notes.
              </Typography>
              <Link to="/account" style={{ textDecoration: 'none' }}>
                <Button variant="contained" color="primary" className={classes.upgradeButton}>
                  Upgrade to PRO
                </Button>
              </Link>
            </React.Fragment>
          ) : (
            <Typography variant="body1" color="textSecondary">
              This feature is only available to PRO members. Please contact your account
              administrator to have your account upgraded to PRO.
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  } else if (!loading) {
    content = (
      <Card>
        <CardHeader title="Brokers" />
        <Divider />
        <BrokersTable
          brokers={brokers}
          pagination={pagination}
          onPageChange={(event, newPage) => handlePageChange(event, newPage)}
          onPerPageChange={handlePerPageChange}
          onSearchClick={(event, newSearch) => handleSearchClick(event, newSearch)}
          onSearchCancelClick={handleSearchCancelClick}
          currentSearch={search}
          isFiltered={isFiltered}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          broker_notes={broker_notes}
          notes_loading={notes_loading}
          onExpandBroker={handleExpandBroker}
        />
      </Card>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        {content}
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    brokers: state.broker.brokers,
    loading: state.broker.loading,
    pagination: state.broker.pagination,
    broker_notes: state.broker.broker_notes,
    notes_loading: state.broker.notes_loading,
    plan_level: state.auth.user.plan_level,
    is_admin: state.auth.user.is_admin
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onFetchBrokers: (page, per_page, search, sort) => dispatch(actions.fetchBrokers(page, per_page, search, sort)),
    onFetchBrokerNotes: (broker_name) => dispatch(actions.fetchBrokerNotes(broker_name))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withErrorHandler(BrokerList, axios));
